import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { idpConfigurationApi } from '../../../services/api/idpConfigurationApi';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, ItemsGrid, ItemCard, ItemCardContent, ActionButtons, ColorDot } from '../styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const GoalStatusTransitions = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({ fromStatusId: '', toStatusId: '', isAllowed: true });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transitionsData, statusesData] = await Promise.all([
        idpConfigurationApi.getAllGoalStatusTransitions(),
        idpConfigurationApi.getAllGoalStatuses()
      ]);
      setItems(transitionsData);
      setStatuses(statusesData);
    } catch (err) {
      showAlert('Failed to load data.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setCurrentItem(item);
      setIsEditing(true);
    } else {
      setCurrentItem({ fromStatusId: '', toStatusId: '', isAllowed: true });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (item) => {
    setCurrentItem(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        await idpConfigurationApi.updateGoalStatusTransition(currentItem.id, currentItem);
        showAlert('Transition updated successfully', 'success');
      } else {
        await idpConfigurationApi.createGoalStatusTransition(currentItem);
        showAlert('Transition created successfully', 'success');
      }
      handleCloseDialog();
      await fetchData();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} transition.`, 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await idpConfigurationApi.deleteGoalStatusTransition(currentItem.id);
      showAlert('Transition deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchData();
    } catch (err) {
      showAlert(err.message || 'Failed to delete transition.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusName = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    return status?.name || 'Unknown';
  };

  const getStatusColor = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    return status?.color || '#666';
  };

  const filteredItems = items.filter(item => {
    const fromName = item.fromStatus?.name || getStatusName(item.fromStatusId);
    const toName = item.toStatus?.name || getStatusName(item.toStatusId);
    const search = (searchTerm || '').toLowerCase();
    return fromName.toLowerCase().includes(search) || toName.toLowerCase().includes(search);
  });

  if (loading && items.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/idp-configuration')}>
            <FaArrowLeft /> Back to IDP Configuration
          </BackButton>
          <Title>Goal Status Transitions</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={true} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Transitions"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                  }
                }}
              />
            </Box>

            <HeaderButton
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Transition
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredItems.length}
            </span>
            {filteredItems.length === 1 ? 'transition' : 'transitions'} found
          </div>

          {filteredItems.length === 0 ? (
            <EmptyState
              title="No transitions found"
              description={searchTerm ?
                "No transitions match your search criteria." :
                "No status transitions have been configured. Click '+ Add Transition' to define allowed status changes."}
            />
          ) : (
            <ItemsGrid>
              {filteredItems.map((item) => (
                <ItemCard key={item.id}>
                  <ItemCardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Chip
                        size="small"
                        label={item.fromStatus?.name || getStatusName(item.fromStatusId)}
                        sx={{
                          backgroundColor: item.fromStatus?.color || getStatusColor(item.fromStatusId),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                      <ArrowForwardIcon sx={{ color: '#9CA3AF' }} />
                      <Chip
                        size="small"
                        label={item.toStatus?.name || getStatusName(item.toStatusId)}
                        sx={{
                          backgroundColor: item.toStatus?.color || getStatusColor(item.toStatusId),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      <Chip
                        size="small"
                        label={item.isAllowed ? 'Allowed' : 'Not Allowed'}
                        color={item.isAllowed ? 'success' : 'error'}
                        variant="outlined"
                      />
                      <ActionButtons>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(item)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(item)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ActionButtons>
                    </Box>
                  </ItemCardContent>
                </ItemCard>
              ))}
            </ItemsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Transition' : 'Add Transition'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>From Status</InputLabel>
            <Select
              name="fromStatusId"
              value={currentItem.fromStatusId}
              onChange={handleInputChange}
              label="From Status"
            >
              {statuses.map(status => (
                <MenuItem key={status.id} value={status.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ColorDot $color={status.color} />
                    {status.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>To Status</InputLabel>
            <Select
              name="toStatusId"
              value={currentItem.toStatusId}
              onChange={handleInputChange}
              label="To Status"
            >
              {statuses.map(status => (
                <MenuItem key={status.id} value={status.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ColorDot $color={status.color} />
                    {status.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={currentItem.isAllowed}
                onChange={handleInputChange}
                name="isAllowed"
              />
            }
            label="Transition Allowed"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!currentItem.fromStatusId || !currentItem.toStatusId}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this status transition? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GoalStatusTransitions;
